import { Close } from "@radix-ui/react-dialog";
import type { UserProfile, UserProfileRead } from "epsm-api-types/models";
import { useCallback } from "react";

import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import {
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	formatValueCurrency,
	formatValueDate,
} from "@/components/widgets/helpers/FormattingHelpers";
import { useGetStripeRoles } from "@/hooks/useGetUserRoles";
import { cn } from "@/lib/utils";
import { EmailLink } from "@/shared/components/EmailLink";
import { SUPPORT_EMAIL } from "@/shared/constants/common";
import {
	buttonClassNames,
	formHeaderClassNames,
} from "@/shared/constants/customStyleSets";
import type { SubscriptionPeriod, SubscriptionPlan } from "../../../../types";
import { formatSubscriptionPeriod } from "../helpers/formatSubscriptionPeriod";
import { getNewNextPaymentDate } from "../helpers/getNewNextPaymentDate";
import { handleOnConfirm } from "../helpers/handleOnConfirm";
import { useGetBillingPreviewData } from "../hooks/useGetBillingPreviewData";

type Props = {
	onConfirm: (
		plan: SubscriptionPlan | null,
		period: SubscriptionPeriod,
	) => Promise<Error | undefined>;
	newPeriod: SubscriptionPeriod;
	selectedPlan: SubscriptionPlan | null;
	userData: Partial<UserProfile & UserProfileRead> | null;
	otherProps?: React.HTMLAttributes<HTMLDivElement>;
	className?: string;
};

export const BillingPreview: React.FC<Props> = ({
	onConfirm,
	className,
	otherProps,
	newPeriod,
	selectedPlan,
	userData,
}) => {
	const stripeRoles = useGetStripeRoles();
	const currentPeriod = userData?.subscriptionInterval as SubscriptionPeriod;
	const nextPaymentDate = userData?.subscriptionExpiresAt || null;
	const adjustedCurrentPeriod = formatSubscriptionPeriod(currentPeriod);
	const adjustedNewPeriod = formatSubscriptionPeriod(newPeriod);

	const newNextPaymentDate = getNewNextPaymentDate(
		nextPaymentDate,
		newPeriod,
		currentPeriod,
	);

	const { error, setError, isLoading, setIsLoading, preview } =
		useGetBillingPreviewData({
			newPeriod,
			selectedPlan,
		});

	const clickHandler = useCallback(
		() =>
			handleOnConfirm({
				selectedPlan,
				newPeriod,
				onConfirm,
				setError,
				setIsLoading,
			}),
		[newPeriod, onConfirm, selectedPlan, setIsLoading, setError],
	);

	return (
		<div
			{...otherProps}
			className={cn("py-4", className)}
			data-testid="billing-preview"
		>
			<DialogHeader>
				<DialogTitle
					className={formHeaderClassNames}
					data-testid="dialog-title"
				>
					You are about to change your current plan!
				</DialogTitle>
			</DialogHeader>

			<DialogDescription className="text-center text-foreground mb-4">
				Please confirm the details below.
			</DialogDescription>

			{preview && (
				<ul className="flex flex-col gap-2 mb-6">
					<li className="text-bold">
						Current plan:{" "}
						<span className="text-gold">
							{adjustedCurrentPeriod} {stripeRoles[0]?.toUpperCase()}
						</span>
					</li>

					<li className="text-bold">
						Renewal date:{" "}
						<span className="text-gold">
							{formatValueDate(nextPaymentDate, "N/A")}
						</span>
					</li>

					<li className="text-bold">
						New plan:{" "}
						<span className="text-gold">
							{adjustedNewPeriod} {selectedPlan?.toUpperCase()}
						</span>
					</li>

					<li className="text-bold">
						New renewal date:{" "}
						<span className="text-gold">{newNextPaymentDate}</span>
					</li>

					<li className="text-bold">
						Total:{" "}
						<span className="text-gold">
							{formatValueCurrency(preview.total / 100, "N/A")}
						</span>
					</li>

					<li className="text-bold">
						Total excluding tax:{" "}
						<span className="text-gold">
							{formatValueCurrency(preview.total_excluding_tax / 100, "N/A")}
						</span>
					</li>
				</ul>
			)}

			<DialogFooter>
				<div className={cn("flex flex-col items-center justify-center w-full")}>
					{isLoading ? (
						<Spinner />
					) : (
						<div className="flex items-center gap-4">
							<Close asChild>
								<Button
									type="button"
									variant="secondary"
									className={buttonClassNames}
									disabled={isLoading || !preview}
								>
									Cancel
								</Button>
							</Close>

							<Button
								type="button"
								variant="secondary"
								className={buttonClassNames}
								disabled={isLoading || !preview}
								onClick={clickHandler}
							>
								Confirm
							</Button>
						</div>
					)}

					{error && (
						<div className="flex flex-col items-center mt-4">
							<p className="text-red-500 mt-4 left-0 right-0 text-center">
								{error}
							</p>

							<p className="text-red-500 left-0 right-0 text-center">
								<EmailLink email={SUPPORT_EMAIL} />.
							</p>
						</div>
					)}
				</div>
			</DialogFooter>
		</div>
	);
};
